import React, { useState, useEffect, useRef, useCallback } from "react";
import * as Mui from "@mui/material";
import { useFetch } from "services/hooks/useFetch";
import {
  themes,
  scrollTop,
  drawerStyle,
  stickyHeader,
  tableRowSize,
  StatusBadge,
  displayFlexItem,
  getStatusFlow,
} from "services/constants";
import { useSelector } from "react-redux";
import { InfoMessage } from "components/CustomComponents/CustomButton";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { Loader } from "components/Loader";
import { OTPreApprovalHeader } from "../../Headers";
import { Typography, Box, Paper, Drawer } from "@mui/material";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { CustomButton } from "components/CustomComponents";
import { showToast } from "services/utils/Status";
import { CreateOTPreApproval } from "./CreateOTPreApproval";
import { ViewOTPreApproval } from "./ViewOTPreApproval";
import { StatusRow } from "components/CustomComponents/StatusRow";
import { ArrowRight } from "@mui/icons-material";

export const MyAOvertimePreApproval = () => {
  const { domain, userScreenAccess, topbarCompanyId, userDetails } =
    useSelector((state) => state.tokenReducer);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [otPreApprovalDetails, setOTPreApprovalDetails] = useState([]);
  const containerRef = useRef(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedId, setSelectedId] = useState("");
  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
  const [isViewPanelOpen, setIsViewPanelOpen] = useState(false);
  const [handleLoader, setHandleLoader] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [expandedData, setExpandedData] = useState("");

  const {
    result: otPreAppData,
    setLoad: reCallApi,
    loading,
  } = useFetch(
    `${domain}My_Zone/Apply/Over_time/ot-pre-approval/?user_id=${userDetails?.id}`
  );

  let check_access = checkEditorPermission(
    userScreenAccess?.module_details,
    "Apply",
    "OT Pre-Approval"
  );

  const handleCreateOpen = () => setIsCreatePanelOpen(true);
  const handleCreateClose = () => setIsCreatePanelOpen(false);

  const handleViewOpen = (item) => {
    if (!item) return; // safety check to prevent undefined issues
    setSelectedItem(item);
    setSelectedId(item?.id);
    setIsViewPanelOpen(true);
  };

  const handleViewClose = () => setIsViewPanelOpen(false);

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleToggle = (index, value) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
    setExpandedData(value);
  };

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  useEffect(() => {
    if (otPreAppData?.length !== 0 && handleLoader) {
      const item = otPreAppData.find((d) => d.id === selectedId);
      if (item) setSelectedItem(item);
    }
  }, [selectedId, handleLoader, otPreAppData]);

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setOTPreApprovalDetails(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage]
  );

  const NewPayrunButton = (
    <CustomButton
      actionFuntion={() => {
        if (check_access) {
          handleCreateOpen();
        } else {
          showToast(
            "error",
            "Access denied: You are not authorized to create shift"
          );
        }
      }}
      actionName="New"
      typeName="button"
    />
  );

  useEffect(() => {
    if (topbarCompanyId) {
      reCallApi(true);
      setHandleLoader(false);
    }
  }, [reCallApi, topbarCompanyId]);

  return !handleLoader && loading ? (
    <Loader />
  ) : (
    <React.Fragment>
      <Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            Overtime Pre-Approval
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View all your pending and history of overtime requests awaiting
            manager review or approval.
          </Typography>
        </Box>

        <Paper sx={{ width: "100%", height: "78vh" }}>
          <CustomFilter
            title="Overtime Pre-Approval Records"
            filterHeaders={OTPreApprovalHeader}
            data={otPreAppData}
            filteredData={otPreApprovalDetails}
            onFilteredDataChange={handleFilteredDataChange}
            screenAccess={check_access}
            newButtonComponent={NewPayrunButton}
          />

          <Mui.Grid sx={{ ml: 2, mr: 2 }}>
            <Mui.TableContainer ref={containerRef} sx={{ maxHeight: 590 }}>
              <Mui.Table className="border">
                <Mui.TableHead sx={stickyHeader}>
                  <Mui.TableRow sx={tableRowSize}>
                    {OTPreApprovalHeader?.map((value) => (
                      <Mui.TableCell
                        sx={{
                          color: themes.headLine,
                          fontWeight: "bold",
                        }}
                      >
                        {value?.name}
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                </Mui.TableHead>

                <Mui.TableBody>
                  {otPreApprovalDetails?.length === 0 ? (
                    <InfoMessage dataLength={otPreAppData} />
                  ) : (
                    otPreApprovalDetails
                      ?.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      ?.map((value, index) => (
                        <React.Fragment key={index}>
                          <Mui.TableRow sx={tableRowSize}>
                            {OTPreApprovalHeader.map((head) => (
                              <Mui.TableCell
                                key={head.key}
                                onClick={() => {
                                  if (head.key === "expand") {
                                    handleToggle(index, value);
                                  } else {
                                    handleViewOpen(value);
                                    setExpandedIndex(null);
                                  }
                                }}
                              >
                                {head.key === "expand" ? (
                                  <ArrowRight
                                    sx={{
                                      transition: "0.3s",
                                      transform:
                                        expandedIndex === index
                                          ? "rotate(90deg)"
                                          : "rotate(0deg)",
                                      cursor: "pointer",
                                    }}
                                  />
                                ) : head.key === "status" ? (
                                  <Mui.Box sx={displayFlexItem}>
                                    <StatusBadge
                                      color={value?.color_code}
                                      label={value?.status || "N/A"}
                                    />
                                  </Mui.Box>
                                ) : (
                                  value[head.key] ?? "N/A"
                                )}
                              </Mui.TableCell>
                            ))}
                          </Mui.TableRow>

                          {expandedIndex === index && (
                            <Mui.TableRow>
                              <Mui.TableCell
                                colSpan={OTPreApprovalHeader.length}
                                sx={{ p: 2 }}
                              >
                                {getStatusFlow(expandedData)[
                                  expandedData?.status
                                ]?.map((item, i, arr) => (
                                  <StatusRow
                                    key={i}
                                    status={item.status}
                                    user={item.user}
                                    date={item.date}
                                    showConnector={i !== arr.length - 1}
                                  />
                                ))}
                              </Mui.TableCell>
                            </Mui.TableRow>
                          )}
                        </React.Fragment>
                      ))
                  )}
                </Mui.TableBody>
              </Mui.Table>
            </Mui.TableContainer>

            <Box sx={{ mt: 1 }}>
              {otPreApprovalDetails?.length > 25 && (
                <Mui.TablePagination
                  className="custom-pagination"
                  component="div"
                  rowsPerPageOptions={[25, 50, 75, 100]}
                  count={otPreApprovalDetails?.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              )}
            </Box>
          </Mui.Grid>
        </Paper>
      </Box>

      {/* DRAWERS */}
      <Drawer
        anchor="right"
        open={isCreatePanelOpen || isViewPanelOpen}
        onClose={() => {
          isCreatePanelOpen ? handleCreateClose() : handleViewClose();
        }}
        sx={drawerStyle}
      >
        {isCreatePanelOpen ? (
          <CreateOTPreApproval
            onClose={handleCreateClose}
            reCallApi={reCallApi}
            setHandleLoader={setHandleLoader}
          />
        ) : isViewPanelOpen ? (
          <ViewOTPreApproval
            onClose={handleViewClose}
            editOTPreAppDetails={selectedItem}
            reCallApi={reCallApi}
            setGetId={setSelectedId}
            setIsViewPanelOpen={(v) => setIsViewPanelOpen(v)}
            setHandleLoader={setHandleLoader}
            screenAccess={check_access}
          />
        ) : null}
      </Drawer>
    </React.Fragment>
  );
};
